import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useFlashMessage } from '../context/FlashMessageContext';
import { API_CONFIG } from '../config/api';
import { Fingerprint } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import './Dashboard.css';
import './Registration.css';

const PROFILE_EDIT_TOKEN_KEY = 'profileEditToken';

function ProfileUpdate() {
  const { user, token, loading } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const { showSuccess, showError } = useFlashMessage();

  const [securityQuestionsConfigured, setSecurityQuestionsConfigured] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [grandfatherFirstName, setGrandfatherFirstName] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [mothersFirstName, setMothersFirstName] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_CONFIG.BASE_URL}/user/security-questions-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSecurityQuestionsConfigured(res.data.configured))
      .catch(() => setSecurityQuestionsConfigured(false));
  }, [token]);

  const storeTokenAndNavigate = (profileEditToken) => {
    sessionStorage.setItem(PROFILE_EDIT_TOKEN_KEY, profileEditToken);
    showSuccess('Verification successful! You can now update your profile.');
    navigate('/dashboard/profile-update/edit');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!firstName.trim()) errors.firstName = 'Please enter your first name.';
    if (!grandfatherFirstName.trim()) errors.grandfatherFirstName = "Please enter your grandfather's first name.";
    if (!placeOfBirth.trim()) errors.placeOfBirth = 'Please enter your place of birth.';
    if (!mothersFirstName.trim()) errors.mothersFirstName = "Please enter your mother's first name.";
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) return;

    if (!securityQuestionsConfigured) {
      setIsSettingUp(true);
      try {
        const res = await axios.post(
          `${API_CONFIG.BASE_URL}/user/setup-security-questions`,
          { firstName, grandfatherFirstName, placeOfBirth, mothersFirstName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { profileEditToken } = res.data;
        storeTokenAndNavigate(profileEditToken);
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        showError(msg);
      } finally {
        setIsSettingUp(false);
      }
    } else {
      setIsVerifying(true);
      try {
        const res = await axios.post(
          `${API_CONFIG.BASE_URL}/user/verify-profile-edit`,
          { firstName, grandfatherFirstName, placeOfBirth, mothersFirstName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const { profileEditToken } = res.data;
        storeTokenAndNavigate(profileEditToken);
      } catch (err) {
        const errCode = err.response?.data?.error;
        const msg = err.response?.data?.message || err.message;
        if (errCode === 'security_questions_incorrect') {
          showError('Incorrect answers. Please try again.');
        } else {
          showError(msg);
        }
      } finally {
        setIsVerifying(false);
      }
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return null;
  }

  const isSetupMode = securityQuestionsConfigured === false;
  const isLoadingStatus = securityQuestionsConfigured === null;

  return (
    <DashboardLayout activeNav="profile-update">
        <div className="dashboard-section">
          <h2><Fingerprint size={24} /> Profile Update</h2>
          {isLoadingStatus ? (
            <p className="update-biometric-description">Loading...</p>
          ) : isSetupMode ? (
            <p className="update-biometric-description">
              Set up your security questions (one-time). These will be used to verify your identity when updating your profile in the future.
            </p>
          ) : (
            <p className="update-biometric-description">
              Answer the security questions below to verify your identity, then you can update your phone, email, and biometric.
            </p>
          )}
          {!isLoadingStatus && (
            <form onSubmit={handleSubmit} className="update-biometric-form">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); setValidationErrors((prev) => { const next = { ...prev }; delete next.firstName; return next; }); }}
                  placeholder="Enter your first name"
                  autoComplete="given-name"
                  className={validationErrors.firstName ? 'input-error' : ''}
                />
                {validationErrors.firstName && (
                  <span className="form-error">{validationErrors.firstName}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="grandfatherFirstName">Grandfather's First Name</label>
                <input
                  id="grandfatherFirstName"
                  type="text"
                  value={grandfatherFirstName}
                  onChange={(e) => { setGrandfatherFirstName(e.target.value); setValidationErrors((prev) => { const next = { ...prev }; delete next.grandfatherFirstName; return next; }); }}
                  placeholder="Enter your grandfather's first name"
                  autoComplete="off"
                  className={validationErrors.grandfatherFirstName ? 'input-error' : ''}
                />
                {validationErrors.grandfatherFirstName && (
                  <span className="form-error">{validationErrors.grandfatherFirstName}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="placeOfBirth">Place of Birth</label>
                <input
                  id="placeOfBirth"
                  type="text"
                  value={placeOfBirth}
                  onChange={(e) => { setPlaceOfBirth(e.target.value); setValidationErrors((prev) => { const next = { ...prev }; delete next.placeOfBirth; return next; }); }}
                  placeholder="Enter your place of birth"
                  autoComplete="off"
                  className={validationErrors.placeOfBirth ? 'input-error' : ''}
                />
                {validationErrors.placeOfBirth && (
                  <span className="form-error">{validationErrors.placeOfBirth}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="mothersFirstName">Mother's First Name</label>
                <input
                  id="mothersFirstName"
                  type="text"
                  value={mothersFirstName}
                  onChange={(e) => { setMothersFirstName(e.target.value); setValidationErrors((prev) => { const next = { ...prev }; delete next.mothersFirstName; return next; }); }}
                  placeholder="Enter your mother's first name"
                  autoComplete="off"
                  className={validationErrors.mothersFirstName ? 'input-error' : ''}
                />
                {validationErrors.mothersFirstName && (
                  <span className="form-error">{validationErrors.mothersFirstName}</span>
                )}
              </div>
              <button type="submit" className="update-biometric-continue-btn" disabled={isSettingUp || isVerifying}>
                {isSetupMode
                  ? (isSettingUp ? 'Saving...' : 'Save & Continue')
                  : (isVerifying ? 'Verifying...' : 'Verify')}
              </button>
            </form>
          )}
        </div>
    </DashboardLayout>
  );
}

export default ProfileUpdate;
export { PROFILE_EDIT_TOKEN_KEY };
